
import { useState } from 'react';
import { CivicEducationItem } from '@/types/civic-education';
import { usePagination } from '@/hooks/polls/usePagination';

interface UseCivicEducationPaginationProps {
  items: CivicEducationItem[];
}

export const useCivicEducationPagination = ({ items }: UseCivicEducationPaginationProps) => {
  const [sortField, setSortField] = useState<keyof CivicEducationItem>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter items based on search term
  const filteredItems = items.filter(item => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      item.title.toLowerCase().includes(searchLower) ||
      item.description.toLowerCase().includes(searchLower) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchLower)) ||
      (item.country_code && item.country_code.toLowerCase().includes(searchLower))
    );
  });

  // Sort the filtered items
  const sortedItems = [...filteredItems].sort((a, b) => {
    const fieldA = a[sortField];
    const fieldB = b[sortField];
    
    // Handle different field types
    if (typeof fieldA === 'string' && typeof fieldB === 'string') {
      return sortDirection === 'asc'
        ? fieldA.localeCompare(fieldB)
        : fieldB.localeCompare(fieldA);
    }
    
    if (fieldA instanceof Date && fieldB instanceof Date) {
      return sortDirection === 'asc'
        ? fieldA.getTime() - fieldB.getTime()
        : fieldB.getTime() - fieldA.getTime();
    }
    
    // Default comparison for other types
    if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
    if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Set up pagination
  const pagination = usePagination({
    totalItems: sortedItems.length,
    initialPage: 1,
    itemsPerPage: 10
  });

  const paginatedItems = pagination.paginatedItems(sortedItems);

  // Handle sort changes
  const handleSort = (field: keyof CivicEducationItem) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return {
    paginatedItems,
    pagination,
    searchTerm,
    setSearchTerm,
    sortField,
    sortDirection,
    handleSort,
    totalItems: filteredItems.length
  };
};
